import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { faSearch, faTrash } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Observable, Subscription, combineLatest, first, map, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { UserService } from 'src/app/core/services/user.service';
import { indicate } from 'src/app/operators';
import { FilterDataService } from 'src/app/search/services/filter.service';
import { FilterablePropertyFactory } from 'src/app/shared/components/factory/filterable-property-factory.service';
import { EsraPipingAPIClient, FilterType, FilterableProperty, PipingRevisionViewModel, PipingSearchFilterModel, PipingSearchResultViewModel, RevisionCreateViewModel, RevisionCreationSubjectViewModel, SearchRequest, SimpleRevisionViewModel } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
    selector: 'app-create-revision',
    templateUrl: './create-revision.component.html',
    styleUrls: ['./create-revision.component.scss'],
    standalone: false
})
export class CreateRevisionComponent implements OnInit, OnDestroy {
  private readonly subscription = new Subscription();
  private readonly filtersDefinition: PipingSearchFilterModel[] = [];
  /** Full option lists from the first unfiltered search — used to prevent filter options from cascading/narrowing. */
  private readonly _baseFilterOptions: Map<string, string[]> = new Map<string, string[]>();
  protected selectedItems: PipingSearchResultViewModel[] = [];
  readonly faSearch = faSearch;
  readonly faTrashCan = faTrash;

  loadingFilters$ = new BehaviorSubject<boolean>(false);
  searching$ = new BehaviorSubject<boolean>(false);
  saving$ = new BehaviorSubject<boolean>(false);
  latestFilterSelection$ = new BehaviorSubject<string>('');
  facetValues$ = new BehaviorSubject<PipingSearchFilterModel[]>([]);
  filters$: Observable<PipingSearchFilterModel[]> = this.getFilters();
  pipeResults$: Observable<PipingSearchResultViewModel[]>;
  filterableProperties: FilterableProperty[];
  resetAllFilters$ = new BehaviorSubject<boolean>(false);

  totalCount = 0;
  lastPage = 0;
  currentPage = 0;
  pageSize = 25;
  pageSizes = [2, 5, 10, 25, 50, 100];
  protected readonly maxDescriptionLength = 250;

  matSortActive = "available P&V"
  matSortDirection = "asc"
  orderBy = "";
  searchText = "";
  revisionDescription = ""
  showRatingsColumn = false;
  searchResultsTable = new MatTableDataSource();
  describeComponentTable = new MatTableDataSource();

  creationStep = 0;

  /** Controls which component types are shown in the search results. Both true = show all. */
  showPipes: boolean = true;
  showValves: boolean = true;

  owners: microsoftgraph.User[] = [];
  reviewers: microsoftgraph.User[] = [];
  contributors: microsoftgraph.User[] = [];

  /** Application names available for the override dropdown on valve rows. */
  applicationOptions: string[] = [];

  @ViewChild(MatTable) matTable: MatTable<PipingSearchResultViewModel>;

    columnNamesSorted = new Map([
      ['checkbox', 'checkbox'],
      ['available P&V', 'name'],
      ['BU', 'businessUnits'],
      ['application', 'application'],
      ['component', 'objectType'],
      ['updated on', 'publishDate'],
      ['new revision','revision'],
      ['application override', 'applicationOverride'],
      ['actions', 'actions']
    ]);

  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly filterDataService: FilterDataService,
    private readonly snackBarService: SnackBarService,
    private readonly userService: UserService,
    private readonly router: Router,
    public dialogRef: MatDialogRef<CreateRevisionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    // Load application options for the valve application-override dropdown.
    this.esraPipingAPIClient.getAllCurrentApplications().pipe(first()).subscribe({
      next: (apps) => {
        this.applicationOptions = apps.map(a => a.name).filter(n => !!n);
      }
    });

    this.filtersDefinition.push(new PipingSearchFilterModel({
      filterPropertyName: 'BusinessUnit',
      filterDisplayName: 'BU',
      filterType: 'MULTI_SELECT_NOFILTER',
      options: []
    }));
    this.filtersDefinition.push(new PipingSearchFilterModel({
      filterPropertyName: 'Application',
      filterDisplayName: 'application',
      filterType: 'MULTI_SELECT_NOFILTER',
      options: []
    }));

    this.subscription.add(
      this.filterDataService.currentFiltersSelection$
        .pipe(
          map((filterSelection) => {
            this.filterableProperties = Array.from(filterSelection.values());
            this.currentPage = 0;
            this.search();
          })
        )
        .subscribe()
    );
  }


  public search(): void {
    const request: SearchRequest = new SearchRequest();

    request.perPage = this.pageSize;
    request.page = this.currentPage;
    request.mainSearch = this.wrapSearchTextInWildcardsConditionally(this.searchText);
    request.filters = this.filterableProperties;
    request.orderBy = this.orderBy;
    request.highlightMatches = false;
    this.esraPipingAPIClient
        .searchPipingAndValves(false, request)
        .pipe(
          indicate(this.searching$),
          first(),
          tap((response) => {
            this.searchResultsTable.data = response.results;
            this.facetValues$.next(response.pipingSearchFilters);
            this.totalCount = response.totalResults;
            this.lastPage = Math.ceil(this.totalCount / this.pageSize);
          })
        ).subscribe();
  }

  private wrapSearchTextInWildcardsConditionally(searchText: string): string{
    let returnString = '';
    let notArray = searchText.split("+-");
    let andArray = searchText.split("+");

    if (notArray.length > 1) {
      notArray = notArray.map((element) => element.trim());
      for (let i = 0; i < notArray.length; i ++){
        if (i !== notArray.length - 1){
          returnString = returnString + notArray[i] + "+-";
        } else {
          returnString = returnString + notArray[i];
        }
      }
    }
    else if (andArray.length > 1) {
      andArray = andArray.map((element) => element.trim())
      for (let i = 0; i < andArray.length; i++){
        if (i !== andArray.length - 1){
          returnString = returnString + andArray[i] + "* + ";
        } else {
          returnString = returnString + andArray[i];
        }
      }
    }
    else {
      returnString = searchText + "*";
    }
    return returnString;
  }

  showRatings(){
    this.showRatingsColumn = !this.showRatingsColumn;
  }

  private getFilters(): Observable<PipingSearchFilterModel[]> {
    return combineLatest([
      this.facetValues$,
      this.latestFilterSelection$,
    ]).pipe(
      map(([facets, _latestFilterSelection]) => {
        return this.filtersDefinition
          .map((filter) => {
            // Always restore to base options once initialized — no cascading.
            // Selecting one filter must not narrow the available options in other panels.
            // Use the same stable array reference (not a spread) so Angular does not call
            // ngOnChanges on multi-select-filter when only latestFilterSelection$ re-emits
            // and options haven't actually changed. A new reference would trigger
            // reconstructCheckButtons() before filterDataService.setFilterValue() has run,
            // leaving checkedOptions empty and causing checkboxes to appear unchecked.
            const baseOptions: string[] | undefined = this._baseFilterOptions.get(filter.filterPropertyName ?? '');
            if (baseOptions !== undefined) {
              filter.options = baseOptions;
              return filter;
            }

            // First load: populate options from facets and store as the permanent base.
            const matchingFacet: PipingSearchFilterModel | undefined = facets.find(
              (facet) => facet.filterPropertyName === filter.filterPropertyName
            );
            if (matchingFacet != null) {
              filter.minValue = matchingFacet.minValue;
              filter.maxValue = matchingFacet.maxValue;

              const sortedOptions: string[] = [...(matchingFacet.options ?? [])].sort((a: string, b: string) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
              );
              this._baseFilterOptions.set(filter.filterPropertyName ?? '', sortedOptions);
              filter.options = sortedOptions;
            }

            return filter;
          });
      })
    );
  }

  public sortColumn(event): void {
    this.matSortActive = event.active;
    this.matSortDirection = event.direction;
    let columnName: string = this.columnNamesSorted.get(event.active);
    if(columnName.toUpperCase() === 'BusinessUnits'.toUpperCase()) {
      columnName = 'BusinessUnit';
    }
    const pascalCasing = columnName.charAt(0).toUpperCase() + columnName.substring(1, columnName.length);
    this.orderBy = `${pascalCasing} ${event.direction}`;
    this.search();
  }

  public sortSelectedItems(event): void {
    const columnName: string = this.columnNamesSorted.get(event.active);
    this.selectedItems.sort((item1, item2) => {
      let val1, val2;
      if( columnName === 'component' ) {
        val1 = item1.objectType;
        val2 = item2.objectType;
      } else if( columnName === 'BU' )  {
        val1 = this.getEntity(item1)?.[columnName]?.[0];
        val2 = this.getEntity(item2)?.[columnName]?.[0];
      } else if( columnName === 'publishDate' ){
        val1 = new Date(this.getEntity(item1)?.[columnName]);
        val2 = new Date(this.getEntity(item2)?.[columnName]);
      } else {
        val1 = this.getEntity(item1)?.[columnName];
        val2 = this.getEntity(item2)?.[columnName];
      }

      if (event.direction === 'asc') {
        return val1 > val2 ? 1 : -1;
      } else {
        return val1 < val2 ? 1 : -1;
      }
    })

    this.describeComponentTable = new MatTableDataSource(this.selectedItems);
  }

  public clearSearchText() {
    this.searchText = '';
    this.search();
  }

  public updatePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  callSearch(e: any) {
    if (e.keyCode == 13) this.search();
  }

  ngOnDestroy(): void {
    // Clear the singleton FilterDataService so that leftover selections don't bleed into
    // other pages or pre-populate the filters the next time this dialog is opened.
    // Use the non-emitting variant — the component is being destroyed so there is no need
    // to notify subscribers, and doing so would trigger an unnecessary search() call.
    this.filterDataService.removeAllFiltersDoNotEmit();
    this.subscription.unsubscribe();
  }

  resetFilters() {
    this.resetAllFilters$.next(true);
    this.filterDataService.removeSetOfFilterValues(['businessUnits', 'application', 'businessUnits']);
    // Re-apply the type filter after clearing since it is managed separately from sidebar filters
    this.applyObjectTypeFilter();
  }

  /**
   * Called when the pipe class / valve checkboxes change.
   * Translates the boolean flags into the 'PIPE'/'VALVE' values the BE expects
   * and pushes the filter via filterDataService.
   */
  onTypeFilterChange(): void {
    this.currentPage = 0;
    this.applyObjectTypeFilter();
  }

  private applyObjectTypeFilter(): void {
    if (this.showPipes && this.showValves) {
      // Both selected — remove the filter so the BE returns everything
      this.filterDataService.removeAllFilterValues('ObjectType');
    } else if (this.showPipes) {
      const filterableProperty = FilterablePropertyFactory.createFilterbleProperty(
        FilterType._1,
        'ObjectType',
        'ObjectType',
        ['PIPE']
      );
      this.filterDataService.setFilterValue(filterableProperty);
    } else if (this.showValves) {
      const filterableProperty = FilterablePropertyFactory.createFilterbleProperty(
        FilterType._1,
        'ObjectType',
        'ObjectType',
        ['VALVE']
      );
      this.filterDataService.setFilterValue(filterableProperty);
    } else {
      // Nothing selected — remove the filter (results will be empty server-side anyway)
      this.filterDataService.removeAllFilterValues('ObjectType');
    }
  }

  public popClearedFilter($event: any, filterPropertyName: string) {
    if (this.latestFilterSelection$.getValue() === filterPropertyName) {
      this.latestFilterSelection$.next('');
    }
  }

  public updateCheckboxFilterSelection(
    event: Set<string>,
    filterablePropertyName: string,
    filterDisplayName: string
  ) {

    this.currentPage = 0;
    if (event.size === 0) {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
      this.latestFilterSelection$.next('');
    } else {
      const filterableProperty = FilterablePropertyFactory.createFilterbleProperty(
        FilterType._1,
        filterablePropertyName,
        filterDisplayName,
        Array.from(event)
      );
      this.latestFilterSelection$.next(filterablePropertyName);
      this.filterDataService.setFilterValue(filterableProperty);
    }
  }

  getStandardColumnNames() : string[] {
    return [...this.columnNamesSorted.keys()]
  }

  removeSelectedItem(item): void {
    this.checkboxChanged(false, item);
    this.popuplateDescriptionsTable();
  }

  checkboxChanged(addItem: boolean, item) : void {
    if (addItem) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = this.selectedItems.filter(component => this.getEntity(component)?.id !== this.getEntity(item)?.id);
    }
  }

  //--- Members Start
  addRevisionApproval(user: microsoftgraph.User, role: string): void {
    let users: microsoftgraph.User[];
    if (role === 'owner') {
      users = this.owners;
    } else if (role === 'reviewer') {
      users = this.reviewers;
    } else {
      users = this.contributors;
    }

    if (this.userExistsWithinGivenRole(users, user.id) !== -1) {
      this.snackBarService.showSnackBar(
        true,
        `${user.displayName} is already added to this revision`,
        'mat-snack-bar-error'
      );
    } else {
      users.push(user);
    }
  }

  private userExistsWithinGivenRole(usersList: microsoftgraph.User[], userOid: string): number {
    const index = usersList.findIndex(
      (approver) => approver.id === userOid
    );

    if ( index !== -1 ) {
      return index;
    } else {
      return -1;
    }
  }

  getNameForComponent(element, itemType: string) {
    if (itemType === 'PIPE') {
      return element.name;
    } else {
      return element.tag.substring(0, element.tag.indexOf('_'));
    }
  }

  /**
   * Returns the underlying pipe or valve entity from a search result.
   * Uses the objectType discriminator: 'PIPE' → result.pipe, otherwise → result.valve.
   */
  getEntity(result: PipingSearchResultViewModel): any {
    return result.objectType === 'PIPE' ? result.pipe : result.valve;
  }

  removeMember(userToRemove: microsoftgraph.User, role: string): void {
    if(role === 'owner') {
      this.owners = this.owners.filter( (user) => user.id !== userToRemove.id );
    } else if(role === 'reviewer') {
      this.reviewers = this.reviewers.filter( (user) => user.id !== userToRemove.id );;
    } else {
      this.contributors = this.contributors.filter( (user) => user.id !== userToRemove.id );;
    }
  }

  getCurrentUserName(): string {
    return this.userService.getCurrentUser()?.name;
  }
  //---Members End

  isChecked(item) : boolean  {
    return this.selectedItems.filter(component => this.getEntity(component)?.id === this.getEntity(item)?.id).length !== 0;
  }

  popuplateDescriptionsTable(): void {
    this.describeComponentTable = new MatTableDataSource(this.selectedItems);
  }

  back() {
    this.creationStep--;
  }

  cancelAction(): void {
    this.dialogRef.close();
  }

  next(): void {
    this.creationStep++;
  }

  save(): void {
    const revisionVm:RevisionCreateViewModel = new RevisionCreateViewModel({
      pipeClasses: [],
      valves: [],
      generalDescription: this.revisionDescription,
      authors:this.owners.map(owner => owner.id),
      internalReviewers: this.reviewers.map(reviewer => reviewer.id),
      externalReviewers: this.contributors.map(contributor => contributor.id),
  });
    this.selectedItems.forEach((item:any) => { //using any until we find out where name is being created
      if (item.objectType === 'PIPE') {
        revisionVm.pipeClasses.push(new RevisionCreationSubjectViewModel({
          id: this.getEntity(item)?.id,
          revisionName: item.name,
        }));
      } else {
        revisionVm.valves.push(new RevisionCreationSubjectViewModel({
          id: this.getEntity(item)?.id,
          revisionName: item.name,
          // Only send application when the user explicitly chose an override;
          // undefined means the BE will keep the original application.
          application: (item as any).applicationOverride || undefined,
        }));
      }
    });

    if (revisionVm.pipeClasses.length > 0 || revisionVm.valves.length > 0) {
      const isValveRevision: boolean = revisionVm.valves.length > 0;

      if (isValveRevision) {
        // After creating a valve revision navigate directly to the draft valve edit page.
        // createRevision returns SimpleRevisionViewModel[] with the new revision id;
        // we then fetch unpublished revisions to get the draft valve id.
        this.esraPipingAPIClient.createRevision(revisionVm)
          .pipe(
            indicate(this.saving$),
            first(),
            switchMap((createdRevisions: SimpleRevisionViewModel[]) => {
              return this.esraPipingAPIClient.getUnpublishedRevisions().pipe(
                first(),
                map((revisions: PipingRevisionViewModel[]) => {
                  const newRevisionId: number = createdRevisions[0]?.id;
                  return revisions.find(r => r.id === newRevisionId);
                })
              );
            })
          )
          .subscribe({
            next: (revision: PipingRevisionViewModel | undefined) => {
              this.dialogRef.close();
              const draftValveId: string | undefined = revision?.valves?.[0]?.id;
              if (draftValveId) {
                this.router.navigate(['/piping/valve/edit'], {
                  queryParams: { mode: 'edit', id: draftValveId }
                });
              } else {
                // Fallback: reload dashboard if draft valve id is not found
                location.reload();
              }
            },
            error: (err) => {
              this.snackBarService.showSnackBar(
                true,
                `The revision creation failed\n${err}`,
                'mat-snack-bar-info'
              );
            }
          });
      } else {
        this.esraPipingAPIClient.createRevision(revisionVm).pipe(indicate(this.saving$)).subscribe({
          next: () => {
            this.dialogRef.close();
            location.reload();
          },
          error: (err) => {
            this.snackBarService.showSnackBar(
              true,
              `The revision creation failed\n${err}`,
              'mat-snack-bar-info'
            );
          }
        });
      }
    }
  }
}
